import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { Service } from 'app/models/services';
import { AgenciesService } from 'app/services/agencies/agencies.service';
import { ServicesService } from 'app/services/services/services.service';
import { AwscontrolService, UploadResponse } from 'app/services/aws/awscontrol.service';

@Component({
  selector: 'app-ajouter-services',
  templateUrl: './ajouter-services.component.html',
  styleUrls: ['./ajouter-services.component.scss']
})
export class AjouterServicesComponent implements OnInit {
  service: Service = new Service();
  agenciesList: any[] = [];

  agencyControl = new FormControl('');
  filteredAgencies: Observable<any[]>;

  selectedFiles: FileList | null = null;
  uploadedMainImage: string | null = null;
  videoUrl: string = '';

  isUploading: boolean = false;
  isSubmitting: boolean = false;
  categoryInput: string = '';

  constructor(
    private router: Router,
    private agenciesService: AgenciesService,
    private servicesService: ServicesService,
    private awsService: AwscontrolService
  ) {}

  ngOnInit(): void {
    this.loadAgencies();
    this.service.categoryList = [];

    this.filteredAgencies = this.agencyControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterAgencies(value || ''))
    );

    this.agencyControl.valueChanges.subscribe(value => {
      if (typeof value === 'string' && value.trim() === '') {
        this.service.agencyName = '';
      }
    });
  }

  loadAgencies(): void {
    this.agenciesService.getList().subscribe(
      (data: any) => {
        this.agenciesList = data;
      },
      (error: any) => {
        console.error('Error loading agencies:', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Impossible de charger la liste des agences',
          confirmButtonColor: '#f44336'
        });
      }
    );
  }

  filterAgencies(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.agenciesList.filter(agency =>
      agency.name.toLowerCase().includes(filterValue)
    );
  }

  displayAgencyName(agency: any): string {
    return agency && agency.name ? agency.name : '';
  }

  onAgencySelected(agency: any): void {
    if (agency) {
      this.agencyControl.setValue(agency);
      this.service.agencyName = agency.name;
    }
  }

  // ----- Formatting -----
  formatText(command: string): void {
    if (!this.service.description) this.service.description = '';

    const textarea = document.getElementById('descriptionTextarea') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = this.service.description.substring(start, end);

    let formattedText = '';
    switch (command) {
      case 'bold': formattedText = `<strong>${selectedText}</strong>`; break;
      case 'italic': formattedText = `<em>${selectedText}</em>`; break;
      case 'underline': formattedText = `<u>${selectedText}</u>`; break;
      case 'list':
        const lines = selectedText.split('\n');
        formattedText = '<ul>\n' + lines.map(line => `  <li>${line}</li>`).join('\n') + '\n</ul>';
        break;
    }

    const newText = this.service.description.substring(0, start) + formattedText + this.service.description.substring(end);
    this.service.description = newText;

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
    }, 0);
  }

  // ----- Categories -----
  addCategory(): void {
    if (this.categoryInput.trim()) {
      if (!this.service.categoryList) {
        this.service.categoryList = [];
      }
      if (!this.service.categoryList.includes(this.categoryInput.trim())) {
        this.service.categoryList.push(this.categoryInput.trim());
      }
      this.categoryInput = '';
    }
  }

  removeCategory(index: number): void {
    if (this.service.categoryList) {
      this.service.categoryList.splice(index, 1);
    }
  }

  // ----- Image Upload -----
  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      this.selectedFiles = files;
      this.uploadMainImage();
    }
  }

  private validateImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/webp'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      Swal.fire({ icon: 'warning', title: 'Format non supporté', text: 'JPEG, PNG, JPG, GIF, WEBP uniquement', confirmButtonColor: '#ff9800' });
      return false;
    }
    if (file.size > maxSize) {
      Swal.fire({ icon: 'warning', title: 'Fichier trop volumineux', text: 'Max 5MB', confirmButtonColor: '#ff9800' });
      return false;
    }
    return true;
  }

  uploadMainImage(): void {
    if (!this.selectedFiles || this.selectedFiles.length === 0) return;
    const file = this.selectedFiles[0];
    if (!this.validateImageFile(file)) return;

    this.isUploading = true;
    const folder = 'service-images';

    this.awsService.uploadFile(file, folder).subscribe({
      next: (response: UploadResponse) => {
        this.isUploading = false;
        if (response.success && response.urls && response.urls.length > 0) {
          const url = response.urls[0];
          this.uploadedMainImage = url;
          this.service.imageUrl = url;
          Swal.fire({ icon: 'success', title: 'Succès', text: 'Image téléchargée', timer: 1500, showConfirmButton: false });
        } else {
          Swal.fire({ icon: 'error', title: 'Erreur', text: response.error || 'Échec du téléchargement', confirmButtonColor: '#f44336' });
        }
      },
      error: (err: any) => {
        this.isUploading = false;
        Swal.fire({ icon: 'error', title: 'Erreur', text: 'Impossible de télécharger l\'image', confirmButtonColor: '#f44336' });
      }
    });
  }

  removeMainImage(): void {
    this.uploadedMainImage = null;
    this.service.imageUrl = '';
  }

  // ----- Submit -----
  onSubmit(): void {
    // 1. Check name
    if (!this.service.name || this.service.name.trim() === '') {
      Swal.fire({ icon: 'warning', title: 'Nom requis', text: 'Veuillez saisir un nom pour le service', confirmButtonColor: '#ff9800' });
      return;
    }

    // 2. Get selected agency from the control
    const selectedAgency = this.agencyControl.value;
    if (!selectedAgency || typeof selectedAgency === 'string') {
      Swal.fire({ icon: 'warning', title: 'Agence requise', text: 'Veuillez sélectionner une agence dans la liste', confirmButtonColor: '#ff9800' });
      return;
    }

    // 3. Extract agency name (backend uses agencyName to find the agency)
    const agencyName = selectedAgency.name;
    if (!agencyName) {
      Swal.fire({ icon: 'warning', title: 'Agence invalide', text: 'L\'agence sélectionnée n\'a pas de nom', confirmButtonColor: '#ff9800' });
      return;
    }

    this.isSubmitting = true;

    // Prepare service data - only send fields the backend expects
    const serviceData = {
      name: this.service.name.trim(),
      description: this.service.description || '',
      agencyName: agencyName,   // Use the selected agency's name
      categoryList: this.service.categoryList || [],
      imageUrl: this.service.imageUrl || '',
      videoUrl: this.videoUrl || ''
      // Note: Do NOT send userId because backend uses agencyName to find the user
    };

    console.log('Service data to submit:', serviceData);

    this.servicesService.createService(serviceData).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;

        console.log('Full response from server:', response);

        const serviceId = response.id || response._id || response.serviceId || 'ID inconnu';
        console.log('Created Service ID:', serviceId);

        // Extract agencyId from selectedAgency for navigation
        const agencyId = selectedAgency.id || selectedAgency._id || selectedAgency.agencyId;

        Swal.fire({
          icon: 'success',
          title: 'Succès!',
          text: `Service créé avec succès (ID: ${serviceId}) pour l'agence ${agencyName}`,
          confirmButtonColor: '#4caf50',
          timer: 4000,
          showConfirmButton: true
        }).then(() => {
          this.router.navigate(['/list-Services', agencyId]);
        });
      },
      error: (error: HttpErrorResponse) => {
        this.isSubmitting = false;
        this.handleError(error);
      }
    });
  }

  private handleError(error: HttpErrorResponse): void {
    let errorMessage = 'Une erreur est survenue lors de la création du service';
    if (error.error?.message) errorMessage = error.error.message;
    else if (error.status === 400) errorMessage = 'Données invalides';
    else if (error.status === 500) errorMessage = 'Erreur interne du serveur';

    Swal.fire({ icon: 'error', title: 'Erreur', text: errorMessage, confirmButtonColor: '#f44336' });
  }

  resetForm(): void {
    Swal.fire({
      title: 'Réinitialiser?',
      text: 'Toutes les données seront perdues',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f44336',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Oui'
    }).then((result) => {
      if (result.isConfirmed) {
        this.service = new Service();
        this.service.categoryList = [];
        this.uploadedMainImage = null;
        this.videoUrl = '';
        this.categoryInput = '';
        this.agencyControl.setValue('');
        this.service.agencyName = '';

        const inputs = document.querySelectorAll('input[type="file"]');
        inputs.forEach((i: any) => i.value = '');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['listeServices']);
  }
}